import { useState, useEffect } from 'react'
import ImageUploader from './ImageUploader'
import { removeBackground } from '../services/api'
import { useTelegram } from '../hooks/useTelegram'
import { checkBackendHealth } from '../utils/checkBackend'
import './RemoveBackground.css'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

function RemoveBackground() {
  const [uploadedFile, setUploadedFile] = useState<string | null>(null)
  const [uploadedFileObj, setUploadedFileObj] = useState<File | null>(null)
  const [result, setResult] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)
  const [backendAvailable, setBackendAvailable] = useState<boolean | null>(null)
  const telegram = useTelegram()
  const showAlert = telegram.showAlert

  useEffect(() => {
    // Проверяем доступность бэкенда при загрузке компонента
    checkBackendHealth(API_URL).then(setBackendAvailable)
  }, [])

  const handleUpload = async (_filename: string, url: string, file: File) => {
    setUploadedFile(url)
    setUploadedFileObj(file)
    setResult(null)
  }

  const handleProcess = async () => {
    if (!uploadedFileObj) return

    setProcessing(true)
    setResult(null)
    
    try {
      // Отправляем на обработку
      const resultBlob = await removeBackground(uploadedFileObj)
      
      // Проверяем размер результата
      if (resultBlob.size === 0) {
        throw new Error('Получен пустой результат. Попробуйте другое изображение.')
      }
      
      const resultUrl = URL.createObjectURL(resultBlob)
      setResult(resultUrl)
      showAlert('Фон успешно удален!')
    } catch (error: any) {
      let errorMsg = 'Ошибка при обработке'
      
      if (error.message) {
        errorMsg = error.message
      } else if (error.response?.data) {
        errorMsg = typeof error.response.data === 'string' 
          ? error.response.data 
          : error.response.data.detail || 'Ошибка при обработке'
      }
      
      // Проверяем, запущен ли бэкенд
      if (errorMsg.includes('Network Error') || errorMsg.includes('ECONNREFUSED')) {
        errorMsg = 'Не удалось подключиться к серверу. Убедитесь, что бэкенд запущен на http://localhost:8000'
      }
      
      showAlert(errorMsg)
      console.error('Remove background error:', error)
    } finally {
      setProcessing(false)
    }
  }

  return (
    <div className="remove-bg-container">
      <h2>Удаление фона</h2>
      <p className="description">
        Загрузите изображение, и мы автоматически удалим фон
      </p>

      {backendAvailable === false && (
        <div className="backend-warning">
          ⚠️ Бэкенд недоступен. Убедитесь, что сервер запущен на {API_URL}
        </div>
      )}

      <ImageUploader
        onUploadSuccess={handleUpload}
        label="Выбрать изображение"
      />

      {uploadedFile && (
        <div className="preview-section">
          <div className="images-comparison">
            <div className="image-preview">
              <img src={uploadedFile} alt="Original" />
              <div className="label">Оригинал</div>
            </div>

            {result && (
              <div className="image-preview">
                <img src={result} alt="Result" />
                <div className="label">Результат</div>
              </div>
            )}
          </div>

          <div className="actions">
            <button
              className="process-button"
              onClick={handleProcess}
              disabled={processing}
            >
              {processing ? 'Обработка...' : 'Удалить фон'}
            </button>

            {result && (
              <a
                href={result}
                download={`no-bg-${uploadedFileObj?.name || 'image'}.png`}
                className="download-button"
              >
                Скачать результат
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default RemoveBackground

